import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { galleryApi } from '../api/client'
import { isValidShareCode } from '../utils/validation'

// 响应式列数（与 tailwind 断点一致：<768→2, <1024→3, 否则→4）
function useColumnCount() {
  const get = () => (window.innerWidth >= 1024 ? 4 : window.innerWidth >= 768 ? 3 : 2)
  const [cols, setCols] = useState(get)
  useEffect(() => {
    const onResize = () => setCols(get())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return cols
}

export default function Gallery() {
  const [items, setItems] = useState([])
  const [sort, setSort] = useState('time')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [redeemCode, setRedeemCode] = useState('')
  const [redeemLoading, setRedeemLoading] = useState(false)

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true)
        const { data } = await galleryApi.list({ limit: 100, sort })
        setItems(data.items || [])
      } catch (err) {
        setError(err.response?.data?.error || err.message || '加载失败')
      } finally {
        setLoading(false)
      }
    }
    fetchGallery()
  }, [sort])

  // 手动分列：轮转分配保证阅读顺序为行优先（最新的在最上方一排），
  // 避免 CSS columns 按列填充导致最新内容堆在最左列
  const colCount = useColumnCount()
  const columns = useMemo(() => {
    const cols = Array.from({ length: colCount }, () => [])
    items.forEach((item, i) => cols[i % colCount].push(item))
    return cols
  }, [items, colCount])

  const handleRedeemCode = async () => {
    const code = redeemCode.trim().toUpperCase()
    if (!isValidShareCode(code)) return
    setRedeemLoading(true)
    try {
      const { data } = await galleryApi.getByShareCode(code)
      window.location.href = `/gallery/${data.id}?code=${encodeURIComponent(code)}`
    } catch (err) {
      alert(err.response?.data?.error || '分享码无效或已失效')
    } finally {
      setRedeemLoading(false)
    }
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="relative overflow-hidden rounded-[2rem] bg-creamy p-8 shadow-card md:p-10">
        <span className="pointer-events-none absolute -right-4 -top-4 text-8xl opacity-20">🖼️</span>
        <h1 className="relative text-3xl font-extrabold tracking-tight text-cocoa md:text-4xl">
          拼豆图集
        </h1>
        <p className="relative mt-3 max-w-2xl text-cocoa-light">
          看看大家生成的拼豆作品，给喜欢的图纸点个赞吧~ 也可以输入好友分享码查看私有图纸。
        </p>
        <div className="relative mt-5 flex max-w-md items-center gap-2">
          <input
            type="text"
            value={redeemCode}
            onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
            maxLength={6}
            placeholder="输入 6 位分享码"
            className={`form-input flex-1 text-center uppercase tracking-widest ${redeemCode && !isValidShareCode(redeemCode) ? 'border-red-300' : ''}`}
            onKeyDown={(e) => e.key === 'Enter' && handleRedeemCode()}
          />
          <button
            onClick={handleRedeemCode}
            disabled={redeemLoading || !isValidShareCode(redeemCode)}
            className={`rounded-full bg-blush px-5 py-2 text-sm font-semibold text-white transition hover:bg-blush-dark ${
              isValidShareCode(redeemCode) && !redeemLoading ? 'shadow-soft' : 'cursor-not-allowed opacity-40'
            }`}
          >
            {redeemLoading ? '…' : '查看'}
          </button>
        </div>
      </section>

      <div className="flex items-center gap-2">
        {[
          { key: 'time', label: '最新' },
          { key: 'likes', label: '最热' },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setSort(s.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
              sort === s.key
                ? 'bg-blush text-white shadow-soft'
                : 'border border-lavender/50 bg-white text-cocoa hover:border-blush'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-2xl border border-blush/40 bg-blush-light/30 p-4 text-sm text-cocoa">
          {error}
        </div>
      )}

      {loading ? (
        <div className="py-20 text-center text-cocoa-light">加载中…</div>
      ) : items.length === 0 ? (
        <div className="flex h-96 flex-col items-center justify-center rounded-[2rem] border-2 border-dashed border-lavender/40 bg-white text-center shadow-card">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-blush-light text-5xl shadow-soft">🎀</div>
          <p className="mt-5 text-cocoa-light">图集还是空的，快去生成第一张拼豆图纸吧~</p>
        </div>
      ) : (
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
          {columns.map((col, colIdx) => (
            <div key={colIdx} className="flex min-w-0 flex-col">
              {col.map((item) => (
                <Link
                  key={item.id}
                  to={`/gallery/${item.id}`}
                  className="mb-4 block overflow-hidden rounded-2xl border border-lavender/30 bg-white shadow-sm transition hover:shadow-card"
                >
              <img
                src={item.imageUrl}
                alt={`gallery-${item.id}`}
                className="w-full"
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiB2aWV3Qm94PSIwIDAgMjAwIDIwMCI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNmNWY1ZjUiLz48dGV4dCB4PSIxMDAiIHk9IjEwNSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj7lm77niYflnKbph48vdGV4dD48L3N2Zz4='
                }}
              />
              <div className="p-3">
                {item.title && <p className="mb-1 text-sm font-medium text-cocoa">{item.title}</p>}
                <div className="flex items-center justify-between text-xs text-cocoa-light">
                  <div>
                    {item.brand && <span className="font-medium text-cocoa">{item.brand}</span>}
                    {item.brand && ' · '}
                    {item.width}×{item.height}
                  </div>
                <div className="flex items-center gap-1 text-[10px] text-cocoa-light">
                  <span>♥</span>
                  <span>{item.likeCount}</span>
                </div>
              </div>
            </div>
                </Link>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
